import { Box, Card, Grid, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EventDetails, ExtendedAccident } from '../../../../../backendsdk';
import MapContent from '../../../../../components/Map';
import useCustomer from '../../../../../hooks/customer';
import useIsMobile from '../../../../../hooks/isMobile';
import videoNA from '../../../../../images/video_not_available.png';
import { isEventVideo } from '../../../../../utils/File';
import { isLocationValid } from '../../../../../utils/location';
import palette from '../../../../ColorPalette';
import { GpsTrace } from '../../../Event/Defs';
import { getGpsTraceFromCsv } from '../../../Event/EventDetails';
import EventVideoComponent from '../../../Event/EventVideo';
import { eventToMarker } from '../../../EventMarkers';
import AccidentDriverDetails, { EMPTY_DRIVER_DETAILS } from '../../AccidentDriverDetails';
import AccidentGeneralDetails from '../../AccidentGeneralDetails';

interface DeatilsTabProps {
    details: ExtendedAccident;
    setDetails: CallableFunction;
    eventDetails?: EventDetails;
    handleChange: CallableFunction;
    handlingStatusList: Array<string>;
    allowUpdate: boolean;
    setUpdateCallbacks: CallableFunction;
    setValidateCallbacks: CallableFunction;
    setAlert: CallableFunction;
    sending: boolean;
    setDetailsChanged: CallableFunction;
}

const DetailsTab: React.FC<DeatilsTabProps> = (props: DeatilsTabProps) => {
    const { t } = useTranslation();
    const { details } = props;
    const [editHandlingStatus, setEditHandlingStatus] = useState<boolean>(false);
    const [gpsTrace, setGpsTrace] = useState<Array<GpsTrace>>([]);
    const [gpsRoute, setGpsRoute] = useState<Array<[string, number, CallableFunction]>>([]);
    const [currentLocation, setCurrentLocation] = useState<string>();
    const [fitBoundsCounter] = useState<number>(0);
    const isMobile = useIsMobile();
    const { customerUsers } = useCustomer();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleChange = (target: string, value: any) => {
        props.handleChange((oldDetails: ExtendedAccident) => {
            if (oldDetails && oldDetails[target as keyof ExtendedAccident] !== value) {
                return { ...oldDetails, [target]: value };
            }
            return oldDetails;
        });
    };

    const textOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        handleChange(event.target.name, event.target.value);
    };

    const conditionalTooltip = (component: React.ReactNode) => {
        if (details.accident_id === 0) {
            return (
                <Tooltip title={t('content.accidents.form.please_save')} followCursor>
                    <Box>{component}</Box>
                </Tooltip>
            );
        } else {
            return component;
        }
    };

    let videoUrl = '';
    if (props.eventDetails) {
        videoUrl = Object.entries(props.eventDetails.artifact_path)
            .map(([fileName, fileUrl]) => (isEventVideo(fileName) ? fileUrl : ''))
            .filter((e) => e != '')[0];
    }

    useEffect(() => {
        // get gps file
        if (props.eventDetails) {
            const artifactUrl = Object.entries(props.eventDetails.artifact_path).find(([name]) =>
                name.includes('gps.csv'),
            )?.[1];
            if (artifactUrl) {
                getGpsTraceFromCsv(artifactUrl)
                    .then((gpsTrace) => {
                        setGpsTrace(gpsTrace);
                    })
                    .catch(() => {
                        setGpsTrace([]);
                    });
            } else {
                setGpsTrace([]);
            }
        }
    }, [props.eventDetails]);

    useEffect(() => {
        if (gpsTrace.length > 0) {
            const gpsTraceRoute: Array<[string, number, CallableFunction]> = gpsTrace.reduce((acc, entry) => {
                if (!isNaN(entry.lat) && !isNaN(entry.lng)) {
                    acc.push([`${entry.lat}:${entry.lng}`, 0, () => palette.accent]);
                }
                return acc;
            }, [] as Array<[string, number, CallableFunction]>);
            setGpsRoute(gpsTraceRoute);
            if (gpsTraceRoute.length > 0) {
                setCurrentLocation(gpsTraceRoute[0][0]);
            }
        } else {
            setGpsRoute([]);
        }
    }, [gpsTrace]);

    // Calculate event route and markers for map
    const eventRoute: Array<[string, number, CallableFunction]> | undefined = props.eventDetails?.route
        ? props.eventDetails.route.split(',').map((location) => [location.toString(), 0, () => palette.accent])
        : undefined;
    const markers: Array<React.ReactNode> = [];
    if (props.eventDetails) {
        if (!!props.eventDetails.location) {
            markers.push(eventToMarker(props.eventDetails.location, props.eventDetails.type, 1));
        } else {
            const eventEstimatedLocation = eventRoute ? eventRoute[Math.floor(eventRoute.length / 2)][0] : undefined;
            markers.push(eventToMarker(eventEstimatedLocation, props.eventDetails.type, 1));
        }
    }

    const route = gpsRoute.length > 0 ? gpsRoute : eventRoute;
    const displayMap =
        props.eventDetails != undefined &&
        details.event == props.eventDetails?.event_id &&
        ((route !== undefined && route.length > 0 && route.every((r) => isLocationValid(r[0]))) ||
            (props.eventDetails.location && isLocationValid(props.eventDetails.location)));

    return (
        <Box sx={{ p: 1, height: isMobile ? '100%' : undefined }}>
            <Grid container spacing={1} columns={2}>
                {isMobile ? (
                    <>
                        <Grid item xs={2}>
                            <Card sx={{ p: 1 }}>
                                {props.eventDetails != undefined && details.event == props.eventDetails?.event_id ? (
                                    <EventVideoComponent
                                        eventDetails={props.eventDetails}
                                        eventId={props.eventDetails.event_id}
                                        eventType={props.eventDetails.type}
                                        artifact={videoUrl}
                                        viewMode="1x1+3x1"
                                        controls
                                        gpsTrace={gpsTrace}
                                        currentLocation={currentLocation}
                                        setCurrentLocation={setCurrentLocation}
                                    />
                                ) : (
                                    <Box
                                        style={{
                                            backgroundColor: palette.primary,
                                            position: 'relative',
                                            aspectRatio: '4 / 3',
                                        }}
                                    >
                                        <img
                                            src={videoNA}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                position: 'absolute',
                                                zIndex: 2,
                                                display: 'block',
                                                objectFit: 'contain',
                                            }}
                                        />
                                    </Box>
                                )}
                            </Card>
                        </Grid>
                        {displayMap ? (
                            <Grid item xs={2}>
                                <Card sx={{ p: 1 }}>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            aspectRatio: '8 / 3',
                                            height: '215px',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <MapContent
                                            zoom={16}
                                            route={route}
                                            location={currentLocation}
                                            eventLocation={props.eventDetails?.location}
                                            markers={markers}
                                            fitBoundsCounter={fitBoundsCounter}
                                            full
                                        />
                                    </Box>
                                </Card>
                            </Grid>
                        ) : null}
                    </>
                ) : null}
                <Grid item xs={2} sm={1}>
                    <Card sx={{ p: 1 }}>
                        <Box>
                            <AccidentGeneralDetails
                                handleChange={handleChange}
                                textOnChange={textOnChange}
                                details={details}
                                setDetails={props.setDetails}
                                editHandlingStatus={editHandlingStatus}
                                setEditHandlingStatus={setEditHandlingStatus}
                                handlingStatusList={props.handlingStatusList}
                                setValidateCallbacks={props.setValidateCallbacks}
                                customerUsers={customerUsers}
                                allowUpdate={props.allowUpdate}
                                setAlert={props.setAlert}
                                sending={props.sending}
                            />
                        </Box>
                    </Card>
                    <Card sx={{ p: 1, mt: 1 }}>
                        {conditionalTooltip(
                            <AccidentDriverDetails
                                accident_id={details.accident_id}
                                details={details.driver ? details.driver : EMPTY_DRIVER_DETAILS}
                                setUpdateCallbacks={props.setUpdateCallbacks}
                                setValidateCallbacks={props.setValidateCallbacks}
                                allowUpdate={props.allowUpdate}
                                sending={props.sending}
                                setDetailsChanged={props.setDetailsChanged}
                            />,
                        )}
                    </Card>
                </Grid>
                <Grid item xs={2} sm={1}>
                    {!isMobile ? (
                        <Card sx={{ p: 1, mb: 1 }}>
                            {props.eventDetails != undefined && details.event == props.eventDetails?.event_id ? (
                                <EventVideoComponent
                                    eventDetails={props.eventDetails}
                                    eventId={props.eventDetails.event_id}
                                    eventType={props.eventDetails.type}
                                    artifact={videoUrl}
                                    viewMode="1x1+3x1"
                                    controls
                                    gpsTrace={gpsTrace}
                                    currentLocation={currentLocation}
                                    setCurrentLocation={setCurrentLocation}
                                />
                            ) : (
                                <img src={videoNA} style={{ width: '100%', aspectRatio: '8 / 3', display: 'block' }} />
                            )}
                        </Card>
                    ) : null}
                    {!isMobile && displayMap ? (
                        <Card sx={{ p: 1, mb: 1 }}>
                            <Box
                                sx={{
                                    width: '100%',
                                    aspectRatio: '8 / 3',
                                    height: '215px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    overflow: 'hidden',
                                }}
                            >
                                <MapContent
                                    zoom={16}
                                    route={route}
                                    location={currentLocation}
                                    eventLocation={props.eventDetails?.location}
                                    markers={markers}
                                    fitBoundsCounter={fitBoundsCounter}
                                    full
                                />
                            </Box>
                        </Card>
                    ) : null}
                </Grid>
            </Grid>
        </Box>
    );
};

export default DetailsTab;
